import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Card, Badge, Spinner, Alert, Button, Modal } from "react-bootstrap";
import { AiOutlineEye } from "react-icons/ai";

const statusVariant = (status) => {
  switch (status) {
    case "uploaded": return "warning";
    case "approved": return "primary";
    case "paid": return "success";
    case "rejected": return "danger";
    default: return "secondary";
  }
};

const statusBorderClass = (status) => {
  switch (status) {
    case "uploaded": return "border-warning";
    case "approved": return "border-primary";
    case "paid": return "border-success";
    case "rejected": return "border-danger";
    default: return "border-secondary";
  }
};

const statusBgClass = (status) => {
  switch (status) {
    case "uploaded": return "bg-warning bg-opacity-10";
    case "approved": return "bg-primary bg-opacity-10";
    case "paid": return "bg-success bg-opacity-10";
    case "rejected": return "bg-danger bg-opacity-10";
    default: return "bg-secondary bg-opacity-10";
  }
};

const formatAmount = (value) => {
  if (value == null || value === "") return "—";
  const n = parseFloat(value);
  return Number.isNaN(n) ? "—" : n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const DriverMyInvoicesPage = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageModal, setImageModal] = useState({
    show: false,
    invoiceId: null,
    imageUrl: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token");
    axios
      .get("/api/driver-invoices/my", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (!cancelled && res.data?.success) setInvoices(res.data.invoices || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || err.message || t("driverInvoiceErrorGeneric"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [t]);

  const statusLabel = (status) => {
    const key = `invoiceStatus_${status}`;
    const label = t(key);
    return label !== key ? label : status;
  };

  const statusHint = (inv) => {
    if ((inv.status === "approved" || inv.status === "paid") && inv.ksef_number) {
      const viaKsef = t("invoiceStatusHint_approved_ksef");
      if (viaKsef && viaKsef !== "invoiceStatusHint_approved_ksef") return viaKsef;
    }
    const key = `invoiceStatusHint_${inv.status}`;
    const hint = t(key);
    return hint !== key ? hint : "";
  };

  const openImage = useCallback(
    async (inv) => {
      setImageModal({ show: true, invoiceId: inv.id, imageUrl: null, loading: true, error: null });
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(`/api/driver-invoices/${inv.id}/image`, {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const blob = res.data;
        if (blob && blob.size > 0) {
          const url = URL.createObjectURL(blob);
          setImageModal((prev) => ({ ...prev, imageUrl: url, loading: false }));
        } else {
          setImageModal((prev) => ({ ...prev, loading: false, error: t("invoiceImageNotFound") }));
        }
      } catch (err) {
        const msg = err.response?.status === 403
          ? t("invoiceImageForbidden")
          : (err.response?.data?.error || err.message || t("driverInvoiceErrorGeneric"));
        setImageModal((prev) => ({ ...prev, loading: false, error: msg }));
      }
    },
    [t]
  );

  const closeImage = useCallback(() => {
    setImageModal((prev) => {
      if (prev.imageUrl) URL.revokeObjectURL(prev.imageUrl);
      return { show: false, invoiceId: null, imageUrl: null, loading: false, error: null };
    });
  }, []);

  if (loading) {
    return (
      <div className="container-fluid px-3 py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2 text-muted">{t("loading")}</p>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3 py-4">
      <h2 className="mb-3">{t("myInvoices")}</h2>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {invoices.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5 text-muted">
            {t("myInvoicesEmpty")}
          </Card.Body>
        </Card>
      ) : (
        <div className="d-flex flex-column gap-3">
          {invoices.map((inv) => (
            <Card
              key={inv.id}
              className={`border-3 shadow w-100 ${statusBorderClass(inv.status)} ${statusBgClass(inv.status)}`}
              style={{ borderRadius: "12px" }}
            >
              <Card.Body className="py-3 px-3">
                <div className="d-flex flex-row flex-wrap align-items-start justify-content-between gap-2 mb-2">
                  <div className="d-flex flex-column align-items-start">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="fw-bold fs-5">#{inv.id}</span>
                      <Badge bg={statusVariant(inv.status)} className="text-uppercase">
                        {statusLabel(inv.status)}
                      </Badge>
                      {inv.ksef_number && (inv.status === "approved" || inv.status === "paid") && (
                        <Badge bg="secondary">{t("invoiceApprovedViaKsefBadge")}</Badge>
                      )}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => openImage(inv)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openImage(inv); } }}
                        className="text-primary d-flex align-items-center"
                        style={{ fontSize: "1.75rem", cursor: "pointer" }}
                        title={t("invoiceViewImage")}
                        aria-label={t("invoiceViewImage")}
                      >
                        <AiOutlineEye />
                      </span>
                    </div>
                    {inv.uploaded_at && (
                      <span className="small text-muted mt-1">
                        {new Date(inv.uploaded_at).toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    )}
                  </div>
                  <div className="d-flex flex-column align-items-end text-end">
                    <div className="d-flex justify-content-between gap-3 align-items-center">
                      <span className="text-muted small">{t("invoiceVat")}</span>
                      <span className="fw-semibold">{formatAmount(inv.vat_amount)} PLN</span>
                    </div>
                    <div className="d-flex justify-content-between gap-3 align-items-center mt-1">
                      <span className="text-muted small">{t("invoiceRefund")}</span>
                      <span className="fw-bold fs-5 text-success">{formatAmount(inv.refund_amount)} PLN</span>
                    </div>
                  </div>
                </div>
                {statusHint(inv) && (
                  <p className="small mb-0 mt-2 pt-2 border-top border-secondary border-opacity-25">
                    {statusHint(inv)}
                  </p>
                )}
                {inv.status === "rejected" && inv.rejection_reason && (
                  <p className="small mb-0 mt-2 text-danger">
                    <span className="fw-semibold">{t("invoiceRejectionReason")}:</span>{" "}
                    {inv.rejection_reason}
                  </p>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      <Modal show={imageModal.show} onHide={closeImage} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("invoiceViewImage")} #{imageModal.invoiceId != null ? imageModal.invoiceId : ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0 bg-dark">
          {imageModal.loading && (
            <div className="py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-white">{t("loading")}</p>
            </div>
          )}
          {imageModal.error && (
            <div className="py-4 px-3">
              <Alert variant="danger" className="mb-0">{imageModal.error}</Alert>
            </div>
          )}
          {imageModal.imageUrl && !imageModal.loading && !imageModal.error && (
            <img
              src={imageModal.imageUrl}
              alt={t("invoiceViewImage")}
              style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeImage}>
            {t("cancel")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DriverMyInvoicesPage;
